import { describeResource } from '@siteless/resource';
import { z } from 'zod';
import { auth } from '../../config/firebase';

function formatRating(rating: number): string {
  const whole = Math.floor(rating);
  const hasHalf = rating - whole >= 0.5;
  if (hasHalf) {
    return whole > 0 ? `${whole}½` : '½';
  }
  return `${whole}`;
}


let LetterboxdUserSearchResultsSchema = z.array(z.object({
  avatarUrl: z.string(),
  displayName: z.string(),
  id: z.string(),
  memberStatus: z.string(),
  username: z.string(),
}));

export type LetterboxdUserSearchResultsType = z.infer<typeof LetterboxdUserSearchResultsSchema>;

export type LetterboxdUserSearchResultsResult =
  | { status: 'ok'; data: LetterboxdUserSearchResultsType }
  | { status: 'error' };

export let LetterboxdUserSearchResults = describeResource(
  'LetterboxdUserSearchResults',
  {
    get: async (searchText: string): Promise<LetterboxdUserSearchResultsResult | null> => {
      if (!searchText) return null;

      let token = await auth.currentUser?.getIdToken();
      let raw = await window.fetch(
        'https://letterboxdsearchusershandler-exmxstwsha-uc.a.run.app?searchText=' +
        searchText,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      );

      if (!raw.ok) {
        return { status: 'error' };
      }

      let { members } = await raw.json();

      return { status: 'ok', data: LetterboxdUserSearchResultsSchema.parse(members) };
    },
  },
);


export const LetterboxdUserResponseSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  location: z.string().nullable(),
  website: z.string().nullable(),
  favoriteFilms: z.array(z.object({
    id: z.string(),
    name: z.string(),
    year: z.number().nullish(),
    posterUrl: z.string().nullable(),
  })),
  oscarFilmsStatus: z.record(
    z.string(), // TMDB ID
    z.object({
      id: z.string(),
      title: z.string(),
      tmdbId: z.number(),
      watched: z.boolean(),
      liked: z.boolean(),
      review: z.string().nullable(),
      rating: z.number().nullable(),
      formattedRating: z.string().nullable().optional(),
      watchedDate: z.string().nullable().optional(),
    })
  )
});

export type LetterboxdUserResponse = z.infer<typeof LetterboxdUserResponseSchema>;

export type OneUserLetterboxdDataResult =
  | { status: 'ok'; data: LetterboxdUserResponse }
  | { status: 'error' };

export let OneUserLetterboxdData = describeResource(
  'OneUserLetterboxdData',
  {
    get: async (userId?: string): Promise<OneUserLetterboxdDataResult | null> => {

      if (!userId) return null;

      let token = await auth.currentUser?.getIdToken();
      let raw = await window.fetch(
        'https://letterboxdgetoneuserhandler-exmxstwsha-uc.a.run.app?userId=' + userId,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        },
      );

      if (!raw.ok) {
        return { status: 'error' };
      }

      let data = await raw.json();
      let parsed = LetterboxdUserResponseSchema.parse(data);
      for (let key in parsed.oscarFilmsStatus) {
        let film = parsed.oscarFilmsStatus[key];
        film.formattedRating = film.rating !== null ? formatRating(film.rating) : null;
      }
      return { status: 'ok', data: parsed };
    },
  },
);